import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import viewIcon from "../assets/icons/viewIcon.png";
import editIcon from "../assets/icons/edit.png";
import deleteIcon from "../assets/icons/deleteIcon.png";
import changeStatusIcon from "../assets/icons/changeStatusIcon.png";
import addIcon from "../assets/icons/addIcon.png";
import downloadIcon from "../assets/icons/downloadIcon.png";
import filterIcon from "../assets/icons/filterIcon.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import usersIcon from "../assets/icons/users.png";
import conversion from "../assets/icons/conversation.png";
import modelsIcon from "../assets/icons/modelsvg.png";

const BASE_URL = `${process.env.REACT_APP_API_URL}/users`;
const ENTERPRISE_URL = `${process.env.REACT_APP_API_URL}/enterprises`;
const ROLE_URL = `${process.env.REACT_APP_API_URL}/roles`;
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [userRoleName, setUserRoleName] = useState("");
  const [userCounts, setUserCounts] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [adminUsersCount, setAdminUsersCount] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [regularUsersCount, setRegularUsersCount] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const navigate = useNavigate();

  const location = useLocation();

   useEffect(() => {
    const fetchRolesAndUser = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/roles/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const roles = res.data;

         console.log("Fetched roles:", roles);

        setRoleList(roles);

        setUserRoleName(roles.user_role || "User");

      } catch (err) {
        console.error("Error fetching roles or user info", err);
      }
    };

    fetchRolesAndUser();
  }, []);

  const currentUser = JSON.parse(
    localStorage.getItem("user") || sessionStorage.getItem("user")
  );

  // For when user comes via navigation (e.g., from enterprise click)
  const preselectedEnterpriseId = location.state?.enterpriseId || null;

  // Use this only when needed separately
  const currentUserEnterpriseId = currentUser?.enterprise_fid || null;

  useEffect(() => {
    if (preselectedEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: preselectedEnterpriseId,
      }));
    }
  }, [preselectedEnterpriseId]);

  const [formData, setFormData] = useState({
    profilepic: null,
    name: "",
    email: "",
    password: "",
    enterprise_id: "",
    role_id: "",
  });

  const [previewUrl, setPreviewUrl] = useState("");

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchData = async () => {
    try {
      const [roleRes, enterpriseRes] = await Promise.all([
        axios.get(ROLE_URL, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(ENTERPRISE_URL, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRoles(roleRes.data);
      setEnterprises(enterpriseRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchd = async () => {
    try {
      const res = await axios.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: "all" },
      });
      setUsers(res.data.data);
    } catch (error) {
      console.error("Failed to fetch users", error);
      showErrorToast(TOAST_MESSAGES.USER.FETCH_ERROR);
    }
  };

  const fetchStats = async () => {
    try {
      const [userCountRes, userAdminCountsRes] =
        await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/users/count/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/users/user-admin-counts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

      if (!userCountRes.ok) throw new Error("Failed to fetch user counts");
      const userCountData = await userCountRes.json();
      setUserCounts(userCountData);

      if (!userAdminCountsRes.ok)
        throw new Error("Failed to fetch user admin counts");
      const userAdminCountsData = await userAdminCountsRes.json();

      // Correct keys based on your response
      setAdminUsersCount({
        total: userAdminCountsData.admins.total,
        active: userAdminCountsData.admins.active,
        inactive: userAdminCountsData.admins.inactive,
      });

      setRegularUsersCount({
        total: userAdminCountsData.users.total,
        active: userAdminCountsData.users.active,
        inactive: userAdminCountsData.users.inactive,
      });
    } catch (err) {
      console.error("fetchStats error:", err);
      showErrorToast(TOAST_MESSAGES.ENTERPRISE.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchData();
    fetchd();
  }, []);

  const filteredUsers = users
    .filter((user) => {
      if (preselectedEnterpriseId) {
        return user.enterprise_fid === preselectedEnterpriseId;
      }
      return true;
    })
    .filter(
      (user) =>
        user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((user) => {
      if (!dayFilter) return true;
      const createdDate = new Date(user.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return createdDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return createdDate >= yesterday && createdDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });


  useEffect(() => {
    if (showForm && userRoleName === "Admin" && currentUserEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: currentUserEnterpriseId,
      }));
    }
  }, [showForm, userRoleName, currentUserEnterpriseId]);

  const totalEntries = filteredUsers.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredUsers.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const resetForm = () => {
    setFormData({
      profilepic: null,
      name: "",
      email: "",
      password: "",
      enterprise_id: "",
      role_id: "",
    });
    setPreviewUrl("");
    setEditingId(null);
    setShowForm(false);
    setConfirmPassword("");
    setViewMode(false);
  };

  const handleSubmit1 = async (e) => {
    e.preventDefault();

    const { password } = formData;

    // Only validate password if it’s provided (in add or update mode)
    if (password) {
      const hasCapital = /[A-Z]/.test(password);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      const isLengthValid = password.length > 5;

      if (!hasCapital) {
        showErrorToast("Password must contain at least one capital letter.");
        return;
      }

      if (!hasSpecial) {
        showErrorToast("Password must contain at least one special character.");
        return;
      }

      if (!isLengthValid) {
        showErrorToast("Password must be longer than 5 characters.");
        return;
      }

      if (password !== confirmPassword) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_MISMATCH);
        return;
      }
    }

    try {
      const data = new FormData();
      data.append("user_name", formData.name);
      data.append("user_email", formData.email);
      data.append("enterprise_fid", formData.enterprise_id);
      data.append("role_fid", formData.role_id);

      if (password) {
        data.append("user_password", password);
      }

      if (formData.profilepic instanceof File) {
        data.append("user_profile_pic", formData.profilepic);
      }

      if (editingId) {
        await axios.put(`${BASE_URL}/${editingId}`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        showSuccessToast(TOAST_MESSAGES.USER.UPDATE_SUCCESS);
      } else {
        await axios.post(BASE_URL, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        showSuccessToast(TOAST_MESSAGES.USER.CREATE_SUCCESS);
      }

      resetForm();
      fetchData();
      fetchd();
    } catch (err) {
      console.error("Submit error:", err);
      showErrorToast(
        editingId
          ? TOAST_MESSAGES.USER.UPDATE_ERROR
          : TOAST_MESSAGES.USER.CREATE_ERROR
      );
    }
  };

  const handleEdit = (user) => {
    setFormData({
      profilepic: null,
      name: user.user_name,
      email: user.user_email,
      enterprise_id: user.enterprise_fid,
      role_id: user.role_fid,
      password: "",
    });
    setPreviewUrl(
      user.user_profile_pic
        ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
        : ""
    );
    setEditingId(user.user_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (user) => {
    setFormData({
      profilepic: null,
      name: user.user_name,
      email: user.user_email,
      enterprise_id: user.enterprise_fid,
      role_id: user.role_fid,
      password: "",
    });
    setPreviewUrl(
      user.user_profile_pic
        ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
        : ""
    );
    setViewMode(true);
    setShowForm(true);
  };

  const handleStatusChange = async (user) => {
    const newStatus = user.user_status === "1" ? 0 : 1;
    const statusText = newStatus === 1 ? "Active" : "Inactive";

    try {
      await axios.patch(
        `${BASE_URL}/${user.user_id}/status`,
        { user_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchd(); // Refresh the data

      showSuccessToast(`User status changed to ${statusText}`);
    } catch (err) {
      console.error("Status change error:", err);
      showErrorToast(TOAST_MESSAGES.USER.STATUS_CHANGE_ERROR);
    }
  };

  const exportExcel = () => {
    const wsData = [
      ["Name", "Email", "Enterprise", "Role"],
      ...filteredUsers.map((u) => [
        u.user_name,
        u.user_email,
        enterprises.find((ent) => ent.enterprise_id === u.enterprise_fid)
          ?.enterprise_name || "",
        roles.find((role) => role.role_id === u.role_fid)?.role_name || "",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "users.xlsx");
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const statsData = [
    {
      label: "Total Users",
      value: regularUsersCount.total + adminUsersCount.total || 0,
      summary: [
        { label: "Users", value: regularUsersCount.total },
        { label: "Admin", value: adminUsersCount.total },
      ],
      icon: usersIcon,
    },
    {
      label: "Users",
      value: regularUsersCount.total,
      summary: [
        { label: "Active", value: regularUsersCount.active },
        { label: "Inactive", value: regularUsersCount.inactive },
      ],
      icon: usersIcon,
    },
    {
      label: "Admins",
      value: adminUsersCount.total,
      summary: [
        { label: "Active", value: adminUsersCount.active },
        { label: "Inactive", value: adminUsersCount.inactive },
      ],
      icon: usersIcon,
    },
  ];

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0  justify-content-evenly align-items-center">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm"
          >
            <div className="d-flex align-items-center mb-1">
              {item.icon && (
                <img
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "22px",
                    height: "22px",
                    filter: "brightness(4) invert(1)",
                  }}
                />
              )}
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>

            <h2 className="fw-bold">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          User Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Users"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
          >
            <img src={downloadIcon} alt="Download" />
          </button>

          <div className="dropdown">
            <button
              className="btn icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
            >
              <img src={filterIcon} alt="Filter" />
            </button>

            <div className="dropdown-menu p-2" style={{ minWidth: "200px" }}>
              <h6 className="fw-bolder text-black">Time</h6>
              <hr />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label" htmlFor={order}>
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder text-black">By Day</h6>
              <hr />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label" htmlFor={day || "all"}>
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between mt-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => {
                    setCurrentPage(1);
                  }}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark ms-2"
            onClick={() => {
              setEditingId(null);
              setViewMode(false);
              setFormData((prev) => ({
                ...prev,
                enterprise_id: preselectedEnterpriseId || "",
              }));
              setShowForm(true);
            }}
          >
            <img src={addIcon} alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table role-table table-sm w-100">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Enterprise</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((user, i) => (
                  <tr key={i}>
                    <td>
                      {user.user_profile_pic ? (
                        <img
                          src={`${SERVER_URL}/${user.user_profile_pic.replace(
                            /\\/g,
                            "/"
                          )}`}
                          alt="profile"
                          style={{
                            width: 25,
                            height: 25,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 25,
                            height: 25,
                            borderRadius: "50%",
                            backgroundColor: "#3e3e3e",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            fontWeight: "bold",
                          }}
                        >
                          {user.user_name?.charAt(0).toUpperCase() || "U"}
                        </div>
                      )}
                    </td>
                    <td>{user.user_name}</td>
                    <td
                      style={{
                        maxWidth: "190px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {user.user_email}
                    </td>
                    <td>
                      {enterprises.find(
                        (ent) => ent.enterprise_id === user.enterprise_fid
                      )?.enterprise_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td>
                      {roles.find((role) => role.role_id === user.role_fid)
                        ?.role_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>

                    <td>
                      {user.user_status === "1" ? (
                        <span className="">Active</span>
                      ) : (
                        <span className="">Inactive</span>
                      )}
                    </td>
                    <td>{formatDateToUserTimeZone(user.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(user)}
                        title="View"
                      >
                        <img src={viewIcon} alt="View" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(user)}
                        title="Edit"
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleStatusChange(user)}
                        title="Change Status"
                      >
                        <img src={changeStatusIcon} alt="Change Status" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fixed-bottom-footer d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleSubmit1}>
                <div className="modal-header justify-content-center bg-white">
                  <h5 className="modal-title">
                    {viewMode
                      ? "View User details"
                      : editingId
                      ? "Update User details"
                      : "Add User details"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={resetForm}
                    aria-label="Close"
                  >
                    <img src={crossIcon} alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Enterprise</label>
                      <select
                        value={formData.enterprise_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise_id: e.target.value,
                          })
                        }
                        className="form-select"
                        disabled={userRoleName !== "Superadmin"}
                      >
                        <option value="">Select Enterprise</option>
                        {enterprises.map((enterprise) => (
                          <option
                            key={enterprise.enterprise_id}
                            value={enterprise.enterprise_id}
                          >
                            {enterprise.enterprise_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Role</label>
                      <select
                        className="form-select"
                        value={formData.role_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            role_id: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      >
                        <option value="">Select Role</option>
                        {roles
                          .filter((role) =>
                            userRoleName?.toLowerCase() === "superadmin" &&
                            !preselectedEnterpriseId
                              ? true
                              : ["admin", "user"].includes(
                                  role.role_name.toLowerCase()
                                )
                          )
                          .sort((a, b) =>
                            a.role_name.localeCompare(b.role_name)
                          )
                          .map((role) => (
                            <option key={role.role_id} value={role.role_id}>
                              {role.role_name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Email (UserName)</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>

                    {!viewMode && (
                      <>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Password</label>
                          <input
                            type="password"
                            className="form-control"
                            value={formData.password}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                password: e.target.value,
                              })
                            }
                            required={!editingId}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Confirm Password</label>
                          <input
                            type="password"
                            className="form-control"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required={!editingId}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Profile Picture</label>
                          <input
                            type="file"
                            className="file-input-reverse"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              setFormData({
                                ...formData,
                                profilepic: file,
                              });
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setPreviewUrl(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setPreviewUrl("");
                              }
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="canclebtn"
                    onClick={resetForm}
                  >
                    {viewMode ? "Close" : "Cancel"}
                  </button>
                  {!viewMode && (
                    <button type="submit" className="enterprisebtn">
                      {editingId ? "Update" : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
